import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';

export const PlaylistContext = createContext();

export const PlaylistProvider = ({ children, userId }) => {
    const { isAuthenticated, getToken } = useFirebase();
    const [playlists, setPlaylists] = useState([]);

    // Helper: Generate bucket names based on reviewCount.
    const generatePlaylistNames = (reviewCount) => {
        const names = [];
        for (let i = 0; i < reviewCount; i++) {
            if (i === 0) names.push("Today");
            else if (i === 1) names.push("Yesterday");
            else names.push(`${i} Days Ago`);
        }
        return names;
    };

    // --- Deadline & Bucket Logic ---
    const DEADLINE_HOUR = 4;
    const DEADLINE_MINUTE = 0;
    const getBucketForDeadline = (addedDateStr) => {
        if (!addedDateStr) return "Today";
        const addedDate = new Date(addedDateStr);
        const now = new Date();
        let firstDeadline = new Date(addedDate);
        firstDeadline.setHours(DEADLINE_HOUR, DEADLINE_MINUTE, 0, 0);
        if (firstDeadline <= addedDate) {
            firstDeadline.setDate(firstDeadline.getDate() + 1);
        }
        if (now < firstDeadline) return "Today";
        const diffMs = now - firstDeadline;
        const bucketIndex = 1 + Math.floor(diffMs / (24 * 60 * 60 * 1000));
        return bucketIndex === 1 ? "Yesterday" : `${bucketIndex} Days Ago`;
    };

    // --- Refresh (Reassign) Playlists ---
    const refreshPlaylists = useCallback(async () => {
        logger.debug("PlaylistContext - refreshPlaylists called");
        
        try {
            // Skip if no user ID or not authenticated
            if (!userId) {
                logger.debug("PlaylistContext - No user ID available, skipping playlist refresh");
                return;
            }
            
            // Check if user is authenticated before making API calls
            if (!localStorage.getItem('access_token')) {
                logger.debug('PlaylistContext - No access token available, skipping playlist refresh');
                return;
            }
            
            // Use apiClient to handle token refresh automatically
            const response = await apiClient.get('/api/playlists');
            
            if (!response.ok) {
                const errorText = await response.text();
                logger.error("PlaylistContext - Error fetching playlists:", response.status, errorText);
                throw new Error(`Failed to fetch playlists: ${response.status} ${errorText}`);
            }
            
            const data = await response.json();
            logger.debug("PlaylistContext - Retrieved playlists:", data);

            // Fetch user settings to get review days count
            // Default to 4 if not set, make sure it's a number between 1 and 10
            const storedCount = Number(localStorage.getItem("daysOfReview"));
            const reviewCount = (storedCount >= 1 && storedCount <= 10) ? storedCount : 4;
            logger.debug("PlaylistContext - Using reviewCount from settings:", reviewCount);
            const expectedNames = generatePlaylistNames(reviewCount);

            const groupedSentences = {};
            data.forEach(pl => {
                const lang = (pl.language || "default").trim().toLowerCase();
                if (!groupedSentences[lang]) groupedSentences[lang] = [];
                if (pl.sentences && Array.isArray(pl.sentences)) {
                    groupedSentences[lang] = groupedSentences[lang].concat(pl.sentences);
                }
            });

            const newPlaylists = [];
            for (const lang in groupedSentences) {
                expectedNames.forEach(name => {
                    const bucketSentences = groupedSentences[lang].filter(track =>
                        track && track.addedDate && 
                        getBucketForDeadline(track.addedDate).trim().toLowerCase() === name.trim().toLowerCase()
                    );
                    
                    // Only add playlists that have sentences
                    if (bucketSentences.length > 0) {
                        // Find the original playlist to get its ID
                        const originalPlaylist = data.find(p => 
                            (p.language || "default").trim().toLowerCase() === lang && 
                            p.name.trim().toLowerCase() === name.trim().toLowerCase()
                        );
                        
                        newPlaylists.push({
                            language: lang,
                            name: name,
                            sentences: bucketSentences,
                            _id: originalPlaylist?._id
                        });
                    }
                });
            }

            logger.debug("PlaylistContext - Setting playlists:", newPlaylists);
            setPlaylists(newPlaylists);
            
            // Store in localStorage for persistence
            localStorage.setItem('cached_playlists', JSON.stringify(newPlaylists));
        } catch (error) {
            logger.error("PlaylistContext - Error refreshing playlists:", error);
            
            // Try to use cached playlists if available
            const cached = localStorage.getItem('cached_playlists');
            if (cached) {
                try {
                    const parsedCache = JSON.parse(cached);
                    if (Array.isArray(parsedCache) && parsedCache.length > 0) {
                        logger.debug("PlaylistContext - Using cached playlists");
                        setPlaylists(parsedCache);
                    }
                } catch (e) {
                    logger.error("Failed to parse cached playlists", e);
                }
            }
        }
    }, [isAuthenticated]);

    useEffect(() => {
        logger.debug("PlaylistContext - Initial mount, isAuthenticated:", isAuthenticated);
        
        // First try to load from localStorage for immediate display
        const cached = localStorage.getItem('cached_playlists');
        if (cached) {
            try {
                const parsedCache = JSON.parse(cached);
                if (Array.isArray(parsedCache) && parsedCache.length > 0) {
                    logger.debug("PlaylistContext - Using cached playlists on init");
                    setPlaylists(parsedCache);
                }
            } catch (e) {
                logger.error("Failed to parse cached playlists", e);
            }
        }
        
        // Then refresh from server
        refreshPlaylists();
        
        // Set up infrequent refresh - only every 4 hours since playlists rarely change
        // Playlists are primarily updated at 4am based on deadline bucket logic
        const REFRESH_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
        logger.debug(`PlaylistContext - Setting refresh interval to ${REFRESH_INTERVAL/1000/60/60} hours`);
        
        const refreshInterval = setInterval(() => {
            logger.debug("PlaylistContext - Periodic refresh (4-hour interval)");
            refreshPlaylists();
        }, REFRESH_INTERVAL);
        
        return () => clearInterval(refreshInterval);
    }, [refreshPlaylists]);

    // --- Context Methods for Adding/Updating/Removing ---
    const addPlaylist = async (newPlaylist) => {
        logger.debug("PlaylistContext - addPlaylist called with:", newPlaylist);
        try {
            const response = await apiClient.post('/api/playlists', newPlaylist);
            
            if (!response.ok) {
                const errorText = await response.text();
                logger.error("PlaylistContext - Error adding playlist:", response.status, errorText);
                throw new Error(`Failed to add playlist: ${response.status} ${errorText}`);
            }
            
            const data = await response.json();
            logger.debug("PlaylistContext - Playlist added:", data);
            refreshPlaylists();
        } catch (error) {
            logger.error("PlaylistContext - Error adding playlist:", error);
        }
    };

    const updatePlaylist = async (playlistId, updatedData) => {
        logger.debug("PlaylistContext - updatePlaylist called with id:", playlistId, "data:", updatedData);
        try {
            const response = await apiClient.put(`/api/playlists/${playlistId}`, updatedData);
            
            if (!response.ok) {
                const errorText = await response.text();
                logger.error("PlaylistContext - Error updating playlist:", response.status, errorText);
                throw new Error(`Failed to update playlist: ${response.status} ${errorText}`);
            }
            
            const data = await response.json();
            logger.debug("PlaylistContext - Playlist updated:", data);
            refreshPlaylists();
        } catch (error) {
            logger.error("PlaylistContext - Error updating playlist:", error);
        }
    };

    const addToPlaylist = async (language, sentenceData) => {
        logger.debug("PlaylistContext - addToPlaylist called with language:", language, "sentenceData:", sentenceData);
        
        try {
            // Make sure we have audio
            if (!sentenceData.audioSrc) {
                logger.error("PlaylistContext - No audioSrc in sentenceData!");
                return;
            }
            
            if (!sentenceData.addedDate) {
                sentenceData.addedDate = new Date().toISOString();
            }
            
            const bucket = getBucketForDeadline(sentenceData.addedDate);
            const lang = (language || "default").trim().toLowerCase();
            
            logger.debug("PlaylistContext - Using bucket:", bucket, "language:", lang);

            // Get all playlists with auto token refresh
            const response = await apiClient.get('/api/playlists');
            
            let errorText = '';
            if (!response.ok) {
                try {
                    errorText = await response.text();
                } catch (e) {
                    errorText = `HTTP status ${response.status}`;
                }
                logger.error("PlaylistContext - Error fetching playlists:", response.status, errorText);
                throw new Error(`Failed to fetch playlists: ${response.status} ${errorText}`);
            }
            
            const allPlaylists = await response.json();
            logger.debug("PlaylistContext - Fetched all playlists to find matching one:", allPlaylists.length);
            
            // Find a matching playlist
            const existingPlaylist = allPlaylists.find(
                pl => 
                    (pl.language || "default").trim().toLowerCase() === lang && 
                    pl.name.trim().toLowerCase() === bucket.trim().toLowerCase()
            );
            
            logger.debug("PlaylistContext - Existing playlist found:", existingPlaylist ? `YES (${existingPlaylist._id})` : "NO");

            if (existingPlaylist) {
                // Check if this sentence is already in the playlist to avoid duplicates
                const isDuplicate = existingPlaylist.sentences.some(
                    s => s.targetSentence === sentenceData.targetSentence
                );
                
                logger.debug("PlaylistContext - Is duplicate sentence:", isDuplicate);
                
                if (isDuplicate) {
                    logger.debug("PlaylistContext - Skipping duplicate sentence");
                    return { success: true, message: "Already in playlist" };
                }
                
                if (existingPlaylist.sentences.length < 50) {
                    logger.debug("PlaylistContext - Adding to existing playlist, id:", existingPlaylist._id);
                    const newSentences = [...existingPlaylist.sentences, sentenceData];
                    
                    const updateResponse = await apiClient.put(
                        `/api/playlists/${existingPlaylist._id}`, 
                        { sentences: newSentences }
                    );
                    
                    if (!updateResponse.ok) {
                        try {
                            errorText = await updateResponse.text();
                        } catch (e) {
                            errorText = `HTTP status ${updateResponse.status}`;
                        }
                        logger.error("PlaylistContext - Error updating playlist:", updateResponse.status, errorText);
                        throw new Error(`Failed to update playlist: ${updateResponse.status} ${errorText}`);
                    }
                    
                    logger.debug("PlaylistContext - Successfully updated playlist, refreshing...");
                    refreshPlaylists();
                    return { success: true };
                } else {
                    // Get a properly capitalized language name for the message
                    const languageName = language.charAt(0).toUpperCase() + language.slice(1).toLowerCase();
                    logger.debug(`PlaylistContext - Playlist full (50 items limit) for ${languageName}`);
                    return { 
                        success: false, 
                        message: `Today's playlist limit of 50 files has been reached for ${languageName}.` 
                    };
                }
            } else {
                logger.debug("PlaylistContext - Creating new playlist for language:", lang, "bucket:", bucket);
                
                const newPlaylist = {
                    language: lang,
                    name: bucket,
                    sentences: [sentenceData]
                };
                
                logger.debug("PlaylistContext - New playlist payload:", newPlaylist);
                
                const createResponse = await apiClient.post('/api/playlists', newPlaylist);
                
                if (!createResponse.ok) {
                    try {
                        errorText = await createResponse.text();
                    } catch (e) {
                        errorText = `HTTP status ${createResponse.status}`;
                    }
                    logger.error("PlaylistContext - Error creating playlist:", createResponse.status, errorText);
                    throw new Error(`Failed to create playlist: ${createResponse.status} ${errorText}`);
                }
                
                const result = await createResponse.json();
                logger.debug("PlaylistContext - Successfully created playlist:", result);
                
                refreshPlaylists();
                return { success: true };
            }
        } catch (error) {
            logger.error("PlaylistContext - Error in addToPlaylist:", error);
            
            // Check if the error message contains "HTTP status undefined"
            // This often happens when there's a network error but the item was actually added
            if (error.message && error.message.includes("HTTP status undefined")) {
                refreshPlaylists(); // Refresh the playlists anyway to get latest state
                return { 
                    success: true, 
                    warning: true,
                    message: "Added to playlist, but failed to update status. You may need to refresh the page." 
                };
            }
            
            return { success: false, message: "Error adding to playlist" };
        }
    };

    const removeFromPlaylist = async (language, sentenceData) => {
        logger.debug("PlaylistContext - removeFromPlaylist called with language:", language, "sentenceData:", sentenceData);
        
        try {
            const bucket = getBucketForDeadline(sentenceData.addedDate);
            const lang = (language || "default").trim().toLowerCase();
            
            // Use apiClient to handle token refresh
            const response = await apiClient.get('/api/playlists');
            
            if (!response.ok) {
                const errorText = await response.text();
                logger.error("PlaylistContext - Error fetching playlists:", response.status, errorText);
                throw new Error(`Failed to fetch playlists: ${response.status} ${errorText}`);
            }
            
            const allPlaylists = await response.json();
            
            const existingPlaylist = allPlaylists.find(
                pl =>
                    (pl.language || "default").trim().toLowerCase() === lang &&
                    pl.name.trim().toLowerCase() === bucket.trim().toLowerCase()
            );
            
            if (existingPlaylist) {
                const newSentences = existingPlaylist.sentences.filter(
                    s => s.targetSentence !== sentenceData.targetSentence
                );
                
                const updateResponse = await apiClient.put(
                    `/api/playlists/${existingPlaylist._id}`,
                    { sentences: newSentences }
                );
                
                if (!updateResponse.ok) {
                    const errorText = await updateResponse.text();
                    logger.error("PlaylistContext - Error updating playlist:", updateResponse.status, errorText);
                    throw new Error(`Failed to update playlist: ${updateResponse.status} ${errorText}`);
                }
                
                // Don't call refreshPlaylists() as it regenerates everything
                // Instead, manually update only the modified playlist in the state
                
                setPlaylists(prevPlaylists => {
                    // Create a copy of the previous playlists
                    let updatedPlaylists = [...prevPlaylists];
                    
                    // Find the index of the playlist we're modifying
                    const playlistIndex = updatedPlaylists.findIndex(pl => 
                        pl._id === existingPlaylist._id || 
                        ((pl.language || "default").trim().toLowerCase() === lang && 
                         pl.name.trim().toLowerCase() === bucket.trim().toLowerCase())
                    );
                    
                    if (playlistIndex !== -1) {
                        if (newSentences.length > 0) {
                            // Update the playlist with new sentences if not empty
                            updatedPlaylists[playlistIndex] = {
                                ...updatedPlaylists[playlistIndex],
                                sentences: newSentences
                            };
                        } else {
                            // Remove the playlist if it's empty
                            updatedPlaylists.splice(playlistIndex, 1);
                        }
                    }
                    
                    logger.debug("PlaylistContext - Updated playlists manually after removal:", updatedPlaylists);
                    return updatedPlaylists;
                });
                
                // Also update the cached playlists in localStorage
                const cachedPlaylists = localStorage.getItem('cached_playlists');
                if (cachedPlaylists) {
                    try {
                        const parsedCache = JSON.parse(cachedPlaylists);
                        
                        // Find the index of the playlist we're modifying
                        const cacheIndex = parsedCache.findIndex(pl => 
                            pl._id === existingPlaylist._id || 
                            ((pl.language || "default").trim().toLowerCase() === lang && 
                             pl.name.trim().toLowerCase() === bucket.trim().toLowerCase())
                        );
                        
                        if (cacheIndex !== -1) {
                            if (newSentences.length > 0) {
                                // Update the playlist with new sentences if not empty
                                parsedCache[cacheIndex] = {
                                    ...parsedCache[cacheIndex],
                                    sentences: newSentences
                                };
                            } else {
                                // Remove the playlist if it's empty
                                parsedCache.splice(cacheIndex, 1);
                            }
                        }
                        
                        localStorage.setItem('cached_playlists', JSON.stringify(parsedCache));
                    } catch (e) {
                        logger.error("Failed to update cached playlists", e);
                    }
                }
                
                return { success: true };
            } else {
                logger.debug("PlaylistContext - Playlist not found for removal");
                return { success: false, message: "Playlist not found" };
            }
        } catch (error) {
            logger.error("PlaylistContext - Error removing from playlist:", error);
            return { success: false, message: "Error removing from playlist" };
        }
    };

    return (
        <PlaylistContext.Provider
            value={{
                playlists,
                addPlaylist,
                updatePlaylist,
                addToPlaylist,
                removeFromPlaylist,
                getBucketForDeadline,
                refreshPlaylists
            }}
        >
            {children}
        </PlaylistContext.Provider>
    );
};

export default PlaylistProvider;